import React from 'react';

// Typography Component System for Wishful
// Ensures consistent typography throughout the site

interface TypographyProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

// Heading Components using Noto Sans
// Scale is based on H1: 4.5rem/72px font, 4rem/64px line-height, -0.2rem/-3.2px letter-spacing
export const H1: React.FC<TypographyProps> = ({ children, className = '', style = {} }) => {
  return (
    <h1
      className={`font-black text-[#780000] ${className}`}
      style={{ 
        fontFamily: 'Noto Sans, sans-serif', 
        fontVariationSettings: "'CTGR' 100, 'wdth' 62.5",
        fontSize: '4.5rem',
        lineHeight: '4rem',
        letterSpacing: '-0.2rem',
        ...style 
      }}
    >
      {children}
    </h1>
  );
};

// H2: 75% of H1 (3.375rem/54px font, 4rem line-height, -0.15rem letter-spacing)
export const H2: React.FC<TypographyProps> = ({ children, className = '', style = {} }) => {
  return (
    <h2
      className={`font-black text-[#780000] ${className}`}
      style={{ 
        fontFamily: 'Noto Sans, sans-serif', 
        fontVariationSettings: "'CTGR' 100, 'wdth' 62.5",
        fontSize: '3.375rem',
        lineHeight: '4rem',
        letterSpacing: '-0.15rem',
        ...style 
      }}
    >
      {children}
    </h2>
  );
};

// H3: 56% of H1 (2.5rem/40px font, 4rem line-height, -0.11rem letter-spacing)
export const H3: React.FC<TypographyProps> = ({ children, className = '', style = {} }) => {
  return (
    <h3
      className={`font-black text-[#780000] ${className}`}
      style={{ 
        fontFamily: 'Noto Sans, sans-serif', 
        fontVariationSettings: "'CTGR' 100, 'wdth' 62.5",
        fontSize: '2.5rem',
        lineHeight: '4rem',
        letterSpacing: '-0.11rem',
        ...style 
      }}
    >
      {children}
    </h3>
  );
};

// H4: 42% of H1 (1.9rem/30.4px font, 4rem line-height, -0.08rem letter-spacing)
export const H4: React.FC<TypographyProps> = ({ children, className = '', style = {} }) => {
  return (
    <h4
      className={`font-black text-[#780000] ${className}`}
      style={{ 
        fontFamily: 'Noto Sans, sans-serif', 
        fontVariationSettings: "'CTGR' 100, 'wdth' 62.5",
        fontSize: '1.9rem',
        lineHeight: '4rem',
        letterSpacing: '-0.08rem',
        ...style 
      }}
    >
      {children}
    </h4>
  );
};

// H5: 31% of H1 (1.4rem/22.4px font, 4rem line-height, -0.06rem letter-spacing)
export const H5: React.FC<TypographyProps> = ({ children, className = '', style = {} }) => {
  return (
    <h5
      className={`font-bold text-[#780000] ${className}`}
      style={{ 
        fontFamily: 'Noto Sans, sans-serif', 
        fontVariationSettings: "'CTGR' 0, 'wdth' 100",
        fontSize: '1.4rem',
        lineHeight: '4rem',
        letterSpacing: '-0.06rem',
        ...style 
      }}
    >
      {children}
    </h5>
  );
};

// H6: 23% of H1 (1.05rem/16.8px font, 4rem line-height, -0.05rem letter-spacing)
export const H6: React.FC<TypographyProps> = ({ children, className = '', style = {} }) => {
  return (
    <h6
      className={`font-bold text-[#780000] ${className}`}
      style={{ 
        fontFamily: 'Noto Sans, sans-serif', 
        fontVariationSettings: "'CTGR' 0, 'wdth' 100",
        fontSize: '1.05rem',
        lineHeight: '4rem',
        letterSpacing: '-0.05rem',
        ...style 
      }}
    >
      {children}
    </h6>
  );
};

// Body Text Components using Open Sans
export const BodyLarge: React.FC<TypographyProps> = ({ children, className = '', style = {} }) => {
  return (
    <p
      className={`font-normal text-[24px] leading-normal ${className}`}
      style={{ fontFamily: 'Open Sans, sans-serif', fontVariationSettings: "'wdth' 100", ...style }}
    >
      {children}
    </p>
  );
};

export const Body: React.FC<TypographyProps> = ({ children, className = '', style = {} }) => {
  return (
    <p
      className={`font-normal text-[18px] leading-normal ${className}`}
      style={{ fontFamily: 'Open Sans, sans-serif', fontVariationSettings: "'wdth' 100", ...style }}
    >
      {children}
    </p>
  );
};

export const BodySmall: React.FC<TypographyProps> = ({ children, className = '', style = {} }) => {
  return (
    <p
      className={`font-normal text-[15px] leading-[22px] ${className}`}
      style={{ fontFamily: 'Open Sans, sans-serif', fontVariationSettings: "'wdth' 100", ...style }}
    >
      {children}
    </p>
  );
};

export const Caption: React.FC<TypographyProps> = ({ children, className = '', style = {} }) => {
  return (
    <p
      className={`font-normal text-[14px] leading-[20px] ${className}`}
      style={{ fontFamily: 'Open Sans, sans-serif', fontVariationSettings: "'wdth' 100", ...style }}
    >
      {children}
    </p>
  );
};

export const Label: React.FC<TypographyProps> = ({ children, className = '', style = {} }) => {
  return (
    <p
      className={`font-normal text-[16px] leading-[22px] ${className}`}
      style={{ fontFamily: 'Open Sans, sans-serif', fontVariationSettings: "'wdth' 100", ...style }}
    >
      {children}
    </p>
  );
};

